import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  fetchFoodIngredients,
  fetchFoodMenuDetail,
  updateFoodMenuItem,
  type FoodMenuDetail,
} from "../foodApi";
import { money, pct, COMMON_UNITS, isIncompleteCost } from "../utils/foodFormatters";
import { QueryError } from "../components/QueryError";
import { UnpricedIngredients } from "../components/UnpricedIngredients";

export function MenuDetailView() {
  const queryClient = useQueryClient();
  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  const [draftName, setDraftName] = useState("");
  const [draftCategory, setDraftCategory] = useState("");
  const [draftPrice, setDraftPrice] = useState("");
  const [draftCostOverride, setDraftCostOverride] = useState("");
  const [draftActive, setDraftActive] = useState(true);
  const [recipeRows, setRecipeRows] = useState<
    Array<{ ingredientId: number; quantity: string; unit: string }>
  >([]);

  const ingredientsQuery = useQuery({
    queryKey: ["food", "ingredients"],
    queryFn: fetchFoodIngredients,
  });
  const q = useQuery({
    queryKey: ["food", "menu", id],
    queryFn: () => fetchFoodMenuDetail(id),
    enabled: Number.isFinite(id),
  });
  const d: FoodMenuDetail | undefined = q.data;

  useEffect(() => {
    if (!d) return;
    setDraftName(d.name);
    setDraftCategory(d.category);
    setDraftPrice(d.currentPrice == null ? "" : String(d.currentPrice));
    setDraftCostOverride(d.costOverride == null ? "" : String(d.costOverride));
    setDraftActive(d.isActive);
    setRecipeRows(
      d.ingredients.map((row) => ({
        ingredientId: row.ingredientId,
        quantity: String(row.quantity),
        unit: row.unit,
      })),
    );
  }, [d?.id]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateFoodMenuItem(id, {
        name: draftName.trim(),
        category: draftCategory.trim(),
        currentPrice: draftPrice.trim() ? Number(draftPrice) : null,
        costOverride: draftCostOverride.trim() ? Number(draftCostOverride) : null,
        isActive: draftActive,
        ingredients: recipeRows
          .map((row) => ({
            ingredientId: row.ingredientId,
            quantity: Number(row.quantity),
            unit: row.unit,
          }))
          .filter((row) => Number.isFinite(row.ingredientId) && row.ingredientId > 0),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["food", "menu"] });
      queryClient.invalidateQueries({ queryKey: ["food", "menu", id] });
      queryClient.invalidateQueries({ queryKey: ["food", "analysis"] });
    },
  });

  // Archived ingredients can't be added, but lines that already use one stay
  // (and keep counting toward cost) so archiving never changes a recipe.
  const selectedIds = new Set(recipeRows.map((row) => row.ingredientId));
  const ingredientOptions = (ingredientsQuery.data ?? []).filter(
    (opt) => opt.isActive || selectedIds.has(opt.id),
  );
  const archivedInRecipe = (ingredientsQuery.data ?? []).filter(
    (opt) => !opt.isActive && selectedIds.has(opt.id),
  );

  if (!Number.isFinite(id)) return <QueryError message="Invalid menu id." />;
  if (q.isLoading) return <div className="p-6 text-tribal-muted">Loading menu item...</div>;
  if (q.isError || !d) {
    return <QueryError message={`Failed to load menu item: ${(q.error as Error).message}`} />;
  }

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
        <h2 className="text-lg font-semibold">Menu item detail</h2>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-5">
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">Name</label>
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className="w-full rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">Category</label>
            <input
              value={draftCategory}
              onChange={(e) => setDraftCategory(e.target.value)}
              className="w-full rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">Price ($)</label>
            <input
              value={draftPrice}
              onChange={(e) => setDraftPrice(e.target.value)}
              placeholder="14.99"
              className="w-full rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">Est. Cost ($)</label>
            <input
              value={draftCostOverride}
              onChange={(e) => setDraftCostOverride(e.target.value)}
              placeholder="Overrides recipe cost"
              className="w-full rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-tribal-muted">&nbsp;</label>
            <label className="flex items-center gap-2 py-1.5 text-sm text-tribal-muted">
              <input
                type="checkbox"
                checked={draftActive}
                onChange={(e) => setDraftActive(e.target.checked)}
              />
              Active
            </label>
          </div>
        </div>
        <p className="mt-2 text-sm text-tribal-muted">
          Cost {money(d.cost.totalCost)}
          {d.costOverride != null && " (override)"}
          {" "}({pct(d.cost.foodCostPct)}) · Suggested{" "}
          {money(d.cost.suggestedPrice)}
        </p>
        {isIncompleteCost(d.cost) && (
          <div className="mt-1 text-sm text-amber-400">
            <p>
              {d.cost.unpricedIngredientCount} ingredient(s) have no usable cost, so this cost is too
              low and food cost % is hidden. Give each one an invoice or manual cost in a unit that
              converts to the recipe unit:
            </p>
            <UnpricedIngredients items={d.cost.unpricedIngredients} />
          </div>
        )}
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="mt-3 rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
        >
          {saveMutation.isPending ? "Saving..." : "Save changes"}
        </button>
      </div>
      <section className="rounded-lg border border-tribal-border bg-tribal-surface">
        <div className="border-b border-tribal-border px-4 py-3 text-sm font-medium">
          Recipe builder
        </div>
        <div className="space-y-2 p-4">
          {archivedInRecipe.length > 0 && (
            <p className="text-sm text-amber-400">
              This recipe uses archived ingredient{archivedInRecipe.length !== 1 ? "s" : ""}:{" "}
              {archivedInRecipe.map((i) => i.name).join(", ")}. They still count toward cost.
            </p>
          )}
          {recipeRows.map((row, idx) => (
            <div
              key={`${row.ingredientId}-${idx}`}
              className="grid grid-cols-1 gap-2 sm:grid-cols-4"
            >
              <select
                value={row.ingredientId}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setRecipeRows((prev) =>
                    prev.map((r, i) => (i === idx ? { ...r, ingredientId: value } : r)),
                  );
                }}
                className="rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
              >
                <option value={0}>Select ingredient</option>
                {ingredientOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                    {!opt.isActive ? " (archived)" : ""}
                  </option>
                ))}
              </select>
              <input
                value={row.quantity}
                onChange={(e) =>
                  setRecipeRows((prev) =>
                    prev.map((r, i) => (i === idx ? { ...r, quantity: e.target.value } : r)),
                  )
                }
                placeholder="Quantity"
                className="rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
              />
              <select
                value={row.unit}
                onChange={(e) =>
                  setRecipeRows((prev) =>
                    prev.map((r, i) => (i === idx ? { ...r, unit: e.target.value } : r)),
                  )
                }
                className="rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
              >
                {COMMON_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setRecipeRows((prev) => prev.filter((_, i) => i !== idx))}
                className="rounded bg-white/5 px-2 py-1 text-xs text-tribal-muted hover:text-white"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              setRecipeRows((prev) => [
                ...prev,
                { ingredientId: 0, quantity: "", unit: "each" },
              ])
            }
            className="rounded bg-white/5 px-2 py-1 text-xs text-tribal-muted hover:text-white"
          >
            Add recipe line
          </button>
        </div>
      </section>
    </div>
  );
}
